import { useCallback, useRef } from 'react';

import type { Field, RedactionScope } from '../types';

export type UseVisitFieldSelectionResult = {
   /** Returns the current plain text of the active visit field. */
   getFieldText: () => string;
   /**
    * Returns a selection scope when the user has text selected inside the
    * suneditor contenteditable, or null when nothing useful is selected.
    * Captures paragraph indices from the live DOM for later use in handleRedactionAccept.
    */
   getSelectionRange: () => { start: number; end: number; text: string } | null;
   /**
    * Returns the Field detected from the DOM selection, or null if detection failed.
    * Call after getSelectionRange to get the field the user actually selected text in.
    */
   getDetectedField: () => Field | null;
   /**
    * Replaces the captured paragraph range with the redacted text.
    */
   handleRedactionAccept: (redactedText: string, scope: RedactionScope) => void;
};

type UseVisitFieldSelectionOptions = {
   getVisitFieldText: (field: Field) => string;
   selectedField: () => Field;
   onReplaceField: (field: Field, text: string) => void;
   onReplaceSelectionInField: (
      field: Field,
      startParagraphIndex: number,
      endParagraphIndex: number,
      redactedText: string
   ) => void;
};

/**
 * Walks from a DOM node up to its nearest <p> ancestor within the editor root.
 * Returns null if no <p> is found.
 */
function findContainingParagraph(node: Node | null, editorRoot: Element): HTMLElement | null {
   let current: Node | null = node;
   while (current && current !== editorRoot) {
      if (current instanceof HTMLElement && current.tagName === 'P') {
         return current;
      }
      current = current.parentNode;
   }
   return null;
}

/**
 * Returns the 0-based index of `target` among all direct-child <p> elements
 * of `editorRoot`. Returns -1 if not found.
 */
function getParagraphIndex(target: HTMLElement, editorRoot: Element): number {
   const paragraphs = editorRoot.querySelectorAll(':scope > p');
   for (let i = 0; i < paragraphs.length; i++) {
      if (paragraphs[i] === target) {
         return i;
      }
   }
   return -1;
}

/**
 * Maps a TextEditor data-testid to the corresponding Field type.
 * Returns null if the testId does not match any known field.
 */
const TEST_ID_TO_FIELD: Record<string, Field> = {
   interview: 'INTERVIEW',
   subjectiveAssessment: 'EXAMINATION',
   recommendations: 'RECOMMENDATIONS',
};

function detectFieldFromEditorRoot(editorRoot: Element): Field | null {
   const textEditorWrapper = editorRoot.closest('[data-testcomponent="TextEditor"]');
   if (!textEditorWrapper) {
      return null;
   }
   const testId = textEditorWrapper.getAttribute('data-testid');
   if (!testId) {
      return null;
   }
   return TEST_ID_TO_FIELD[testId] ?? null;
}

/**
 * Detects the field from the currently focused editor (cursor position),
 * even when no text is selected.
 */
function detectFieldFromFocusedEditor(): Field | null {
   const selection = window.getSelection();
   if (!selection || selection.rangeCount === 0) {
      return null;
   }
   const { anchorNode } = selection;
   const anchorEl = anchorNode instanceof Element ? anchorNode : anchorNode?.parentElement;
   const editorRoot = anchorEl?.closest('.sun-editor-editable');
   if (!editorRoot) {
      return null;
   }
   return detectFieldFromEditorRoot(editorRoot);
}

// eslint-disable-next-line max-lines-per-function
export function useVisitFieldSelection(options: UseVisitFieldSelectionOptions): UseVisitFieldSelectionResult {
   /*
    * Captures the 0-based indices of the first and last <p> elements touched
    * by the selection at the moment the user clicks "Redaguj z AI".
    * Used in handleRedactionAccept to replace exactly those paragraphs in the
    * stored HTML without any text-matching or whitespace normalisation.
    */
   const capturedRef = useRef<{ startIndex: number; endIndex: number } | null>(null);
   const detectedFieldRef = useRef<Field | null>(null);

   const getFieldText = useCallback((): string => {
      const field = detectedFieldRef.current ?? options.selectedField();
      return options.getVisitFieldText(field);
   }, [options]);

   const getDetectedField = useCallback((): Field | null => {
      return detectedFieldRef.current;
   }, []);

   // eslint-disable-next-line max-statements
   const getSelectionRange = useCallback((): { start: number; end: number; text: string } | null => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
         capturedRef.current = null;
         detectedFieldRef.current = detectFieldFromFocusedEditor();
         return null;
      }

      const selectedText = selection.toString().trim();
      if (!selectedText) {
         capturedRef.current = null;
         // No text selected but cursor may be inside an editor — detect field from focus
         detectedFieldRef.current = detectFieldFromFocusedEditor();
         return null;
      }

      const range = selection.getRangeAt(0);
      const { startContainer, endContainer } = range;

      // Verify the selection is inside a suneditor contenteditable
      const anchorNode = startContainer;
      const anchorEl = anchorNode instanceof Element ? anchorNode : anchorNode?.parentElement;
      const editorRoot = anchorEl?.closest('.sun-editor-editable');
      if (!editorRoot) {
         capturedRef.current = null;
         detectedFieldRef.current = null;
         return null;
      }

      // Detect which field the selection belongs to
      detectedFieldRef.current = detectFieldFromEditorRoot(editorRoot);

      const startP = findContainingParagraph(startContainer, editorRoot);
      const endP = findContainingParagraph(endContainer, editorRoot);

      if (!startP || !endP) {
         capturedRef.current = null;
         return null;
      }

      const startIndex = getParagraphIndex(startP, editorRoot);
      const endIndex = getParagraphIndex(endP, editorRoot);

      if (startIndex === -1 || endIndex === -1) {
         capturedRef.current = null;
         return null;
      }

      capturedRef.current = {
         startIndex: Math.min(startIndex, endIndex),
         endIndex: Math.max(startIndex, endIndex),
      };

      return {
         start: 0,
         end: selectedText.length,
         text: selectedText,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [options]);

   const handleRedactionAccept = useCallback(
      (redactedText: string, scope: RedactionScope): void => {
         const captured = capturedRef.current;
         const field = detectedFieldRef.current ?? options.selectedField();
         capturedRef.current = null;
         detectedFieldRef.current = null;

         if (captured && scope.kind === 'selection') {
            options.onReplaceSelectionInField(field, captured.startIndex, captured.endIndex, redactedText);
            return;
         }

         // Full-field replacement — formatting loss is acceptable here.
         options.onReplaceField(field, redactedText);
      },
      [options]
   );

   return {
      getFieldText: getFieldText,
      getSelectionRange: getSelectionRange,
      getDetectedField: getDetectedField,
      handleRedactionAccept: handleRedactionAccept,
   };
}
